import React, { useEffect, useState, useRef } from "react";
import { orderApiService } from "../../../service/admin/ordersApiService";
import { useLoader } from "../../../base/context/LoaderProvider";
import { ToastTypes, useMessage } from "../../../base/context/MessageProvider";
import { useNavigate } from "react-router-dom";
import { FaStore } from "react-icons/fa";
import Pagination from "../../../component/library/pagination";
import FailedOrderFilter from "../../../component/views/admin/failedOrders/FailedOrderFilter";
import FailedOrderTable from "../../../component/views/admin/failedOrders/FailedOrderTable";
import ExcelJS from 'exceljs';
import usePermissions from "../../../hooks/usePermissions";
import "./style.css";

const FailedOrders = () => {
  const [failedOrders, setFailedOrders] = useState([]);
  const [ownAccountOrders, setOwnAccountOrders] = useState([]);
  const [resellerAccountOrders, setResellerAccountOrders] = useState([]);
  const [otherSellerOrders, setOtherSellerOrders] = useState([]);
  const [activeTab, setActiveTab] = useState("all");
  const [initialLoading, setInitialLoading] = useState(true);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState({
    totalOrders: 0,
    todayOrders: 0,
    weekOrders: 0,
    ownAccountTotalOrders: 0,
    resellerAccountTotalOrders: 0,
    otherSellerTotalOrders: 0,
  });
  const loader = useLoader();
  const message = useMessage();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const [limit] = useState(50);
  const [page, setPage] = useState(1);
  const [query, setQuery] = useState({});

  const [pages, setPages] = useState({
    currentPage: "",
    totalPages: "",
    totalItems: "",
  });

  // Upload progress state
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);

  // Check if user is a seller (not admin/sub-admin)
  const userType = (localStorage.getItem('userType') || '').toLowerCase();
  const isSeller = userType === 'seller';

  const { canView: canViewPerm, isAdmin } = usePermissions();
  const isAdminUser = typeof isAdmin === 'function' ? isAdmin() : isAdmin;
  const canViewBTPL = isAdminUser || canViewPerm("failed-orders-btpl");
  const canViewReseller = isAdminUser || canViewPerm("failed-orders-reseller");
  const canViewOther = isAdminUser || canViewPerm("failed-orders-other");
  const allowedTabs = [
    ...(canViewBTPL ? ["own"] : []),
    ...(canViewReseller ? ["reseller"] : []),
    ...(canViewOther ? ["other"] : []),
  ];
  const showAllTab = allowedTabs.length > 1;

  useEffect(() => {
    if (!isSeller && allowedTabs.length === 1 && activeTab === "all") setActiveTab(allowedTabs[0]);
    if (!isSeller && activeTab === "all" && !showAllTab && allowedTabs.length > 0) setActiveTab(allowedTabs[0]);
  }, [isSeller, showAllTab, allowedTabs.length, activeTab]);

  useEffect(() => {
    fetchFailedOrders();
  }, [page, query, activeTab]);

  const fetchFailedOrders = () => {
    setLoading(true);
    // Add accountType filter based on active tab
    const accountType = activeTab === 'own' ? 'own' : activeTab === 'reseller' ? 'reseller' : activeTab === 'other' ? 'other' : '';
    
    const queryParams = `?limit=${limit}&page=${page}&search=${
      query.search || ""
    }&orderDateFrom=${query.orderDateFrom || ""}&orderDateTo=${
      query.orderDateTo || ""
    }&createdDateFrom=${query.createdDateFrom || ""}&createdDateTo=${
      query.createdDateTo || ""
    }&accountType=${accountType}`;

    loader.showInlineLoader("Loading failed orders...", "medium");
    orderApiService
      .getFailedOrders(queryParams)
      .then((res) => {
        loader.hideLoader();
        setLoading(false);
        setInitialLoading(false);
        if (res.status.code === 200) {
          setFailedOrders(res.data.orders || []);
          setOwnAccountOrders(res.data.ownAccountOrders || []);
          setResellerAccountOrders(res.data.resellerAccountOrders || []);
          setOtherSellerOrders(res.data.otherSellerOrders || []);
          setPages({
            ...pages,
            currentPage: res.data.currentPage,
            totalPages: res.data.totalPages,
            totalItems: res.data.totalOrders,
          });
          setPage(res.data.currentPage);
          
          // Update stats
          setStats({
            totalOrders: res.data.totalOrders || 0,
            todayOrders: res.data.todayOrders || 0,
            weekOrders: res.data.weekOrders || 0,
            ownAccountTotalOrders: res.data.ownAccountTotalOrders || 0,
            resellerAccountTotalOrders: res.data.resellerAccountTotalOrders || 0,
            otherSellerTotalOrders: res.data.otherSellerTotalOrders || 0,
          });
        } else if (res.status.code === 402) {
          navigate("/login");
        } else if (res.status.code === 404) {
          // No data found - just set empty array, don't show notification
          setFailedOrders([]);
          setOwnAccountOrders([]);
          setResellerAccountOrders([]);
          setOtherSellerOrders([]);
          setPages({
            currentPage: 1,
            totalPages: 0,
            totalItems: 0,
          });
          setStats({
            totalOrders: 0,
            todayOrders: 0,
            weekOrders: 0,
            ownAccountTotalOrders: 0,
            resellerAccountTotalOrders: 0,
            otherSellerTotalOrders: 0,
          });
        } else {
          // Only show error toast for actual errors, not for empty data
          if (res.error?.message && res.error.message !== "Order Not Found") {
            message.showToast(res.error.message, ToastTypes.ERROR);
          }
          setFailedOrders([]);
          setOwnAccountOrders([]);
          setResellerAccountOrders([]);
          setOtherSellerOrders([]);
        }
      })
      .catch((err) => {
        loader.hideLoader();
        setLoading(false);
        setInitialLoading(false);
        // Don't show toast for "not found" type errors
        if (err?.message && !err.message.toLowerCase().includes("not found")) {
          message.showToast(
            err?.message || "Server error. Try again later",
            ToastTypes.ERROR
          );
        }
        setFailedOrders([]);
        setOwnAccountOrders([]);
        setOtherSellerOrders([]);
      });
  };

  const handleQueryChange = (params = {}) => {
    setPage(1);
    setQuery(params);
  };

  // Get data based on active tab - now filtered from backend
  const getDisplayData = () => {
    return failedOrders; // Backend now filters based on accountType
  };

  const getDisplayPages = () => {
    return pages; // Backend returns correct pagination for the filtered data
  };

  const displayData = getDisplayData();
  const displayPages = getDisplayPages();

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = [
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'text/csv'
    ];
    
    if (!allowedTypes.includes(file.type) && !file.name.match(/\.(xlsx|xls|csv)$/i)) {
      message.showToast("Please upload a valid Excel or CSV file", ToastTypes.ERROR);
      return;
    }

    const formData = new FormData();
    formData.append('file', file);

    // Show progress modal
    setUploadProgress(0);
    setIsUploading(true);
    
    // Simulate progress since fetch doesn't support upload progress
    const progressInterval = setInterval(() => {
      setUploadProgress(prev => {
        if (prev >= 90) return prev; // Stop at 90% until upload completes
        return prev + 10;
      });
    }, 200);
    
    try {
      const res = await orderApiService.uploadFailedOrdersWithUserId(formData);
      
      clearInterval(progressInterval);
      setIsUploading(false);
      setUploadProgress(100);
      
      if (res.status.code === 200) {
        const createdCount = res.data?.createdOrders || 0;
        const failedCount = res.data?.failedOrders || 0;
        const userNotFoundCount = res.data?.userNotFoundCount || 0;
        
        let messageText = `Successfully processed: ${createdCount} order(s) created`;
        if (failedCount > 0) {
          messageText += `, ${failedCount} failed order(s)`;
        }
        if (userNotFoundCount > 0) {
          messageText += `. ${userNotFoundCount} user(s) not found`;
        }
        
        message.showToast(messageText, ToastTypes.SUCCESS);
        // Reset to first page and refresh
        setPage(1);
        fetchFailedOrders();
        
        // Reset progress after a short delay
        setTimeout(() => {
          setUploadProgress(0);
        }, 1000);
      } else {
        message.showToast(
          res.error?.message || "Failed to upload orders",
          ToastTypes.ERROR
        );
        setUploadProgress(0);
      }
    } catch (err) {
      clearInterval(progressInterval);
      setIsUploading(false);
      setUploadProgress(0);
      message.showToast(
        err?.message || "Error uploading file. Please try again.",
        ToastTypes.ERROR
      );
    }

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      // Create a new workbook
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('Orders Template');

      // Define headers based on the upload format
      const headers = [
        'India S.No',
        'Entry Date',
        'Order Type',
        'Order Item ID',
        'Panel',
        'User ID',
        'Order Date',
        'Order ID',
        'Product Name',
        'SKU',
        'ASIN',
        'URL',
        'Qty',
        'Order Value',
        'Ship By Date',
        'Buyer Name',
        'Buyer Address',
        'City',
        'State',
        'Pinode',
        'Mob. No.'
      ];

      // Add header row
      const headerRow = worksheet.addRow(headers);

      // Style the header row
      headerRow.eachCell((cell) => {
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FF4472C4' }
        };
        cell.font = {
          color: { argb: 'FFFFFFFF' },
          bold: true,
          size: 11
        };
        cell.alignment = {
          vertical: 'middle',
          horizontal: 'center'
        };
        cell.border = {
          top: { style: 'thin' },
          left: { style: 'thin' },
          bottom: { style: 'thin' },
          right: { style: 'thin' }
        };
      });

      // Add one empty example row
      const exampleRow = worksheet.addRow([]);
      exampleRow.eachCell((cell) => {
        cell.border = {
          top: { style: 'thin' },
          left: { style: 'thin' },
          bottom: { style: 'thin' },
          right: { style: 'thin' }
        };
      });

      // Auto-fit columns
      headers.forEach((header, index) => {
        const column = worksheet.getColumn(index + 1);
        column.width = Math.max(header.length + 2, 12);
      });

      // Generate buffer and create download
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'Failed_Orders_Template.xlsx');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      message.showToast("Template downloaded successfully", ToastTypes.SUCCESS);
    } catch (error) {
      console.error('Template download error:', error);
      message.showToast("Error downloading template. Please try again.", ToastTypes.ERROR);
    }
  };

  return (
    <div className="fo-page">
      {/* Upload Progress Modal */}
      {isUploading && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 9999
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '30px',
            borderRadius: '8px',
            minWidth: '400px',
            maxWidth: '500px',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
          }}>
            <h3 style={{ marginTop: 0, marginBottom: '20px', color: '#333' }}>Uploading File</h3>
            <div style={{
              width: '100%',
              height: '20px',
              backgroundColor: '#e0e0e0',
              borderRadius: '10px',
              overflow: 'hidden',
              marginBottom: '10px'
            }}>
              <div style={{
                width: `${uploadProgress}%`,
                height: '100%',
                backgroundColor: '#4CAF50',
                transition: 'width 0.3s ease',
                borderRadius: '10px'
              }} />
            </div>
            <p style={{ margin: '5px 0', color: '#666', textAlign: 'center' }}>{uploadProgress}% uploaded</p>
            <p style={{ margin: '10px 0 0 0', color: '#999', fontSize: '14px', textAlign: 'center' }}>
              Please wait while we process your file...
            </p>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="fo-content">
        {/* Filter Section */}
        <FailedOrderFilter setQuery={handleQueryChange} />

        {/* Tabs: only show what the role has access to (BTPL / Reseller / Other Sellers) */}
        <div className="fo-tabs">
          {showAllTab && (
            <button
              onClick={() => { setPage(1); setActiveTab("all"); }}
              className={`fo-tab ${activeTab === "all" ? "fo-tab-active" : ""}`}
            >
              Failed Orders ({stats.totalOrders})
            </button>
          )}
          {canViewBTPL && (
            <button
              onClick={() => { setPage(1); setActiveTab("own"); }}
              className={`fo-tab fo-tab-own ${activeTab === "own" ? "fo-tab-active" : ""}`}
            >
              <span className="fo-tab-icon">⭐</span>
              BTPL ({stats.ownAccountTotalOrders})
            </button>
          )}
          {canViewReseller && (
            <button
              onClick={() => { setPage(1); setActiveTab("reseller"); }}
              className={`fo-tab fo-tab-reseller ${activeTab === "reseller" ? "fo-tab-active" : ""}`}
            >
              <FaStore className="fo-tab-icon" />
              Reseller ({stats.resellerAccountTotalOrders})
            </button>
          )}
          {canViewOther && (
            <button
              onClick={() => { setPage(1); setActiveTab("other"); }}
              className={`fo-tab ${activeTab === "other" ? "fo-tab-active" : ""}`}
            >
              Other Sellers ({stats.otherSellerTotalOrders})
            </button>
          )}
          {/* Hide Upload and Download buttons for sellers */}
          {!isSeller && (
            <>
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls,.csv"
                onChange={handleFileUpload}
                style={{ display: 'none' }}
                id="failed-orders-file-upload"
              />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="fo-upload-btn"
              >
                <i className="fas fa-upload"></i>
                Upload File
              </button>
              <button
                type="button"
                onClick={handleDownloadTemplate}
                className="fo-download-template-btn"
              >
                <i className="fas fa-download"></i>
                Download Template
              </button>
            </>
          )}
          <div className="fo-tabs-right">
            <span className="fo-stat-item">Today: <strong>{stats.todayOrders}</strong></span>
            <span className="fo-stat-item">This Week: <strong>{stats.weekOrders}</strong></span>
          </div>
        </div>

        {/* Table Section */}
        <FailedOrderTable 
          data={displayData} 
          pages={displayPages} 
          limit={limit} 
          page={page}
          showOwnAccountBadge={activeTab === "all"}
          showFullExcelData={activeTab === "own" || activeTab === "reseller"}
          onDataChange={fetchFailedOrders}
          isLoading={initialLoading || loading}
          loadingText="Loading failed orders..."
        />

        {/* Pagination */}
        <div className="fo-pagination">
          {displayData && displayData.length > 0 ? (
            <Pagination pages={displayPages} page={page} setPage={setPage} />
          ) : (
            <div className="fo-no-data">
              <i className="fas fa-inbox"></i>
              <p>No failed orders found</p>
              <span>Try adjusting your filters or search criteria</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FailedOrders;
